import { useEffect, useRef, useState } from 'react';

/**
 * Naming a new bot.
 *
 * This replaces a `window.prompt`. Both bot screens opened one, inherited from the
 * first version of the platform console, and it is wrong for three separate reasons
 * rather than one aesthetic one: a native dialog cannot be styled or themed, so it
 * arrives as an unstyled grey box saying "localhost:5173 says" in front of the
 * product; it blocks the whole page, including the render behind it; and it gives no
 * room to validate, explain, or say what happens next. A prompt is a placeholder
 * somebody forgot to replace, and a client showing this to their own customers is
 * right to say so.
 *
 * Shared because both portals create flows and neither should be the one that keeps
 * the dialog.
 */
export function NameAFlow({ busy, onCreate, onCancel }: {
  busy: boolean;
  onCreate: (name: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const input = useRef<HTMLInputElement>(null);

  // Focus lands in the field, which is the one thing the native dialog did do.
  useEffect(() => { input.current?.focus(); }, []);

  const trimmed = name.trim();

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (trimmed) onCreate(trimmed); }}
      className="mb-3 max-w-xl rounded-lg border border-(--color-line) bg-(--color-surface) p-4"
    >
      <label className="block">
        <span className="text-small font-medium text-(--color-muted)">What is this bot called?</span>
        <input
          ref={input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}
          maxLength={80}
          placeholder="Support · after hours · order status"
          className="mt-1.5 w-full rounded-lg border border-(--color-line) bg-(--color-paper) px-3 py-2.5 text-body"
        />
      </label>

      <p className="mt-2 max-w-[70ch] text-small text-(--color-muted)">
        Only your team sees this name. It starts as a draft with a single welcome
        message and answers nobody until you publish it and put it live.
      </p>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={busy || trimmed.length === 0}
          className="rounded-lg bg-(--color-brand) px-4 py-2 text-body font-medium text-white disabled:opacity-40"
        >
          {busy ? 'Creating…' : 'Create and open it'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-(--color-line) px-4 py-2 text-body"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
